import React, { useEffect, useState } from 'react';
import { Modal, Table, Checkbox, Tag, message } from 'antd';
import { apiRequest } from '../../../service/apiRequest';  // your API util
import { showSuccess, showError } from '../../../components/errorHandler';


interface ClassItem {
  class_id: number;
  class_name: string;
  class_status: number;
  com_id: string | null; // null means NOT mapped
}

interface Props {
  visible: boolean;
  onCancel: () => void;
  classesData: ClassItem[];
  organizationId: string; // pass org id to link classes
  onSaved: () => void; // callback after successful save
}

const ClassesOrgMapping: React.FC<Props> = ({
  visible,
  onCancel,
  classesData,
  organizationId,
  onSaved,
}) => {
  const [selectedClassIds, setSelectedClassIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const mappedIds = classesData
      .filter((cls) => cls.com_id !== null)
      .map((cls) => cls.class_id);
    setSelectedClassIds(mappedIds);
  }, [classesData]);

  const onCheckChange = (classId: number, checked: boolean) => {
    setSelectedClassIds((prev) =>
      checked ? [...prev, classId] : prev.filter((id) => id !== classId)
    );
  };

  const columns = [
    {
      title: 'Class Name',
      dataIndex: 'class_name',
      key: 'class_name',
    },
    {
      title: 'Status',
      dataIndex: 'class_status',
      key: 'class_status',
      render: (status: number) =>
        status === 1 ? (
          <Tag color="green">Active</Tag>
        ) : (
          <Tag color="red">Inactive</Tag>
        ),
    },
    {
      title: 'Mapped',
      dataIndex: 'com_id',
      key: 'mapped',
      render: (com_id: string | null) =>
        com_id ? (
          <Tag color="blue">Mapped</Tag>
        ) : (
          <Tag color="default">Not Mapped</Tag>
        ),
    },
    {
      title: 'Select',
      key: 'select',
      render: (_: any, record: ClassItem) => (
        <Checkbox
          checked={selectedClassIds.includes(record.class_id)}
          onChange={(e) => onCheckChange(record.class_id, e.target.checked)}
        />
      ),
    },
  ];

  const handleSave = async () => {
    setLoading(true);
    try {
      // API call - replace URL & payload as needed
      await apiRequest({
        method: 'put',
        url: `/map-classes-to-organization/${organizationId}`, // example endpoint
        data: { class_ids: selectedClassIds },
      });
      showSuccess('Classes mapped successfully');
      onSaved(); // notify parent to refresh or close modal
      onCancel();
    } catch (error) {
      console.error(error);
      showError('Failed to save class mappings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Map Classes to Organization"
      visible={visible}
      onCancel={onCancel}
      onOk={handleSave}
      okText="Save"
      confirmLoading={loading}
      width={600}
    >
      <Table
        rowKey="class_id"
        dataSource={classesData}
        columns={columns}
        pagination={false}
        scroll={{ y: 300 }}
      />
    </Modal>
  );
};

export default ClassesOrgMapping;
